import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackCTA,
  trackPageView,
  trackScrollDepth,
  trackSignupClick,
  trackTimeOnPage,
} from '@/lib/analytics';

const SCROLL_THRESHOLDS = [50, 90] as const;

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    const startedAt = performance.now();
    const firedScrollThresholds = new Set<number>();

    trackPageView(path, document.title);

    const handleScroll = () => {
      const doc = document.documentElement;
      const scrollableHeight = doc.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;
      const percent = Math.round((window.scrollY / scrollableHeight) * 100);

      SCROLL_THRESHOLDS.forEach((threshold) => {
        if (percent >= threshold && !firedScrollThresholds.has(threshold)) {
          firedScrollThresholds.add(threshold);
          trackScrollDepth(threshold, path);
        }
      });
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      const analyticsElement = target.closest<HTMLElement>('[data-analytics-event]');
      if (analyticsElement?.dataset.analyticsEvent === 'cta_click') {
        trackCTA(
          analyticsElement.dataset.analyticsLabel ?? analyticsElement.textContent?.trim() ?? 'cta',
          analyticsElement.dataset.analyticsLocation ?? location.pathname,
          analyticsElement.dataset.analyticsHref ?? analyticsElement.getAttribute('href') ?? undefined,
        );
      }

      const signupLink = target.closest<HTMLAnchorElement>('a[href*="/coach/auth?mode=signup"]');
      if (signupLink) {
        trackSignupClick(location.pathname, signupLink.href);
      }
    };

    const flushTimeOnPage = () => {
      const seconds = (performance.now() - startedAt) / 1000;
      if (seconds >= 5) {
        trackTimeOnPage(seconds, path);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushTimeOnPage();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('click', handleClick);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      flushTimeOnPage();
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname, location.search]);

  return null;
};

export default AnalyticsTracker;
